import { Component, ViewChild } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams } from 'ionic-angular';
import { GridComponent, RowClassArgs } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { dailyLegLoadColumns } from './daily-leg-loads-log-component.config';
import { DailyLegLoadPermissions, LockedDayPermissions } from '../../../models/permissions/permission.models';
import { Subject } from 'rxjs/Subject';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { AppData } from '../../../models/app-data/app-data';
import { PageValidationType, ValidationResult } from '../../../models/projects/validation.models';
import { timer } from 'rxjs/observable/timer';
import { catchError, debounce } from 'rxjs/operators';
import { DailyLegLoad, DailyLegLoadCompleteEditor } from '../../../models/daily-leg-loads/daily-leg-load.models';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { LockDayFilters } from '../../../models/lock-day.models';
import { CompleteDayService } from '../../../services/complete-day/complete-day.service';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { DailyLegLoadService } from '../../../services/daily-leg-loads/daily-leg-load.service';
import { DailyLegLoadValidationService } from '../../../services/daily-leg-loads/daily-leg-load-validation.service';
import { ValidationService } from '../../../services/validation/validation.service';
import { DateService } from '../../../services/date/date.service';
import { DailyLegLoadsManageReadingsPage } from '../daily-leg-load-manage-readings/daily-leg-load-manage-readings';
import { DailyLegLoadGraphPage } from '../daily-leg-load-graph/daily-leg-load-graph';
import { DailyLegLoadTotalExtensionsPage } from '../daily-leg-load-total-extensions/daily-leg-load-total-extensions';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { cloneDeep } from 'lodash';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { DailyLogDatePickerComponent } from '../../shared/components/daily-log-date-picker/daily-log-date-picker';
import { AppConfig } from '../../../app/app.config';
import { ImplementerService } from '../../../services/implementer/implementer.service';

@IonicPage()
@Component({
  selector: 'page-daily-leg-loads-log-component',
  templateUrl: 'daily-leg-loads-log-component.html',
})
export class DailyLegLoadsLogComponentPage {
  @ViewChild('dailyLegLoadGrid') grid?: GridComponent;
  @ViewChild('dailyLogDatePickerComponent') dailyLogDatePickerComponent: DailyLogDatePickerComponent;

  config = AppConfig.settings;

  dailyLegLoadData: DailyLegLoad[] = [];
  columns = dailyLegLoadColumns;
  state: State = {
    sort: [{field: 'date', dir: 'asc'}]
  };

  dayCompleted: boolean;
  dayLockedByDate: boolean = false;
  isImplementerActiveForDay: boolean;
  dailyLegLoadsCompleted: boolean = false;
  allReadingsComplete: boolean;
  currentSetupConfigured: boolean;
  validationResult: ValidationResult = new ValidationResult();

  loading: boolean;
  dataSyncing: boolean;
  latestTransactionId: number;
  initialLoad: boolean;
  firstLoadComplete = false;
  dateBeforeInitialSetup: boolean;
  validationLoading: boolean;
  dailyLogDate: PtLuxonDate = new PtLuxonDate();
  displayNoLoadRequiredMessage: boolean;
  disableRows: boolean;

  dataToUpdate: AppData = new AppData();
  $confirmedChange: Subject<AppData> = new Subject<AppData>();
  leavingPage: boolean = false;
  debounceActive = false;
  requestActive = false;

  permissions: DailyLegLoadPermissions = new DailyLegLoadPermissions();
  lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();

  selectedText: boolean;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    private dailyLogService: DailyLogService,
    private permissionsService: PermissionsService,
    private completeDayService: CompleteDayService,
    private lockedDayService: LockedDayService,
    private dailyLegLoadService: DailyLegLoadService,
    private dailyLegLoadValidatorService: DailyLegLoadValidationService,
    private validationService: ValidationService,
    private dateService: DateService,
    private modalController: ModalController,
    private navHelperService: NavHelperService,
    private timeZoneService: TimeZoneService,
    private permissionService: PermissionsService,
    private implementerService: ImplementerService,
  ) {
    this.navHelperService.currentSetupConfigured.subscribe(currentSetupConfigured => {
      this.currentSetupConfigured = currentSetupConfigured;
    });

    this.navHelperService.updateLegLoadLogRequiresReload(false);

    // reload will be required if the user has added or deleted a recording frequency - so subscribe to that here
    this.navHelperService.legLoadLogRequiresReload.subscribe(async requiresReload => {
      if (requiresReload) {
        await this.ngOnInit();
        this.navHelperService.updateLegLoadLogRequiresReload(false);
      }
    });
  }

  async ngOnInit() {
    this.loading = true;
    this.initialLoad = true;
    this.validationLoading = true;
    this.latestTransactionId = 0;

    const ptDate = await this.dailyLogService.getCurrentLogDay();

    this.permissions = await this.permissionsService.dailyLegLoadPermissions();
    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(ptDate);

    if (ptDate != null) {
      this.dailyLogDate = cloneDeep(ptDate);
    }

    await this.checkImplementerActive();
    this.loadGrid();

    this.$confirmedChange
      .pipe(
        catchError(err => {
          this.debounceActive = false;
          throw (err.error);
        }),
        debounce(() => timer(2000)))
      .subscribe(result => {
        if (!this.leavingPage) {
          this.dataSyncing = true;
          this.dataToUpdate.transactionId = this.latestTransactionId;

          // If requestActive, then previous request hasn't returned yet. If we send the next request before the previous finishes then
          // we will get a race condition and api will error on trying to save second request due to entities being updated from the
          // first request, while mid-processing the second, so don't continue.
          if (!this.requestActive) {
            this.requestActive = true;
            // @ts-ignore
            this.dailyLegLoadService.quickSaveLegLoadRecords(this.dataToUpdate.dailyLegLoads)
              .then(async rec => {
                this.requestActive = false;
                this.dataSyncing = false;
                this.debounceActive = false;
                this.validate();
                this.allReadingsComplete = this.dailyLegLoadData.filter(e => !e.disabled).every(e => e.statusId === 3);

                // we used to check api for these before, but if the user has just made an edit then we can safely assume that both of these are false
                // without needing to make 2 api calls to confirm it (because they get set to false 100% of the time when saving leg loads here)
                this.dailyLegLoadsCompleted = false;
                this.dayCompleted = false;
              });

            this.dataToUpdate = new AppData();
          } else {
            // if request is active then we will wait 1 second and check again, and this repeats until we are safe to send the next request
            setTimeout(() => {
              this.$confirmedChange.next(this.dataToUpdate);
            }, 1000);
          }
        }
      });
  }

  // used for when the user views the validation and then returns to the weather log - because leavingPage will be true if they view validation
  reEnterComponent() {
    this.leavingPage = false;
  }

  // this gets called via the ionViewWillLeave in the parent page daily-leg-loads-log.ts
  // ensures that data gets saved correctly before the user leaves without having to wait for the debounce
  saveBeforeLeavingComponent() {
    this.leavingPage = true; // stop debounce from running after this save

    if (this.dataToUpdate.dailyLegLoads && this.dataToUpdate.dailyLegLoads.length > 0) {
      this.dailyLegLoadService.quickSaveLegLoadRecords(this.dataToUpdate.dailyLegLoads);
    }
  }

  async completeDay() {
    const editor = new DailyLegLoadCompleteEditor();

    editor.date = this.dailyLogDate.dateTime;
    editor.isComplete = true;

    // note that the result editor.date isn't deserialised, because we dont actually use it anywhere
    this.completeDayService.completeDailyLegLoadDay(editor).then(result => {
      this.dailyLegLoadsCompleted = true;
    });
  }

  async checkDayCompleted() {
    const filters = new LockDayFilters();
    filters.date = this.dailyLogDate.dateTime;

    const lockedSummary = await this.lockedDayService.checkDay(filters);
    this.dayLockedByDate = lockedSummary.lockedByDate;


    this.completeDayService.checkDay(filters).then(result => {
      this.dayCompleted = result;
    });

    this.completeDayService.checkDailyLegLoadDay(filters).then(result => {
      this.dailyLegLoadsCompleted = result;
    });
  }

  async checkImplementerActive() {
    this.isImplementerActiveForDay = await this.implementerService
      .implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate));
  }

  async validate() {
    this.validationLoading = true;
    this.dailyLegLoadValidatorService.validateLegLoadLog(this.dailyLegLoadData).then(legLoadValidation => {
      this.validationLoading = false;
      this.loading = false;
      this.validationResult = legLoadValidation;
    });
  }

  dayLockedForEditing() {
    this.checkDayCompleted();
  }

  async cancelChanges() {
    this.loading = true;
    this.grid.loading = true;
    this.dataSyncing = true;
    this.loadGrid();
  }

  async changesSaving() {
    this.disableRows = true;
    this.loading = true;
    this.dataSyncing = true;
    this.grid.loading = true;
  }

  async changesSaved() {
    this.loading = false;
    this.dataSyncing = false;
    this.grid.loading = false;
    this.disableRows = false;
  }

  async dailyLegLoadReadingChanged(row: DailyLegLoad) {
    this.validationLoading = true;
    this.debounceActive = true;

    // set status - even though they will get set on the api save call and overwrite this, this will appear a lot faster for the user
    const result = this.dailyLegLoadService.getUpdatedStatuses([row]);
    row.statusId = result[0].statusId;

    await this.dailyLegLoadService.saveLegLoadMultipleRecords(this.dailyLegLoadData);
    await this.sendToDebounce(row);
  }

  async sendToDebounce(row: DailyLegLoad) {
    this.latestTransactionId++;

    const filteredRecords: DailyLegLoad[] = [];

    for (let i = 0; i < this.dataToUpdate.dailyLegLoads.length; i++) {
      if (this.dataToUpdate.dailyLegLoads[i].id !== row.id) {
        const sameDay = this.dateService.compareDayOffsetsPreApplied(this.dataToUpdate.dailyLegLoads[i].date, row.date);
        if (sameDay) {
          filteredRecords.push(this.dataToUpdate.dailyLegLoads[i]);
        }
      }
    }

    // offsets used to be applied to the dataToUpdate.dailyLegLoads here, but this was moved to just before the api call where it loops
    // through and does it all at once, it needed to move so that records that are edited get added to the array as soon as possible, so
    // if the user enters data and immediately clicks to reload the log, the data entered will be saved fast enough for it to be there when
    // the log reloads

    this.dataToUpdate.dailyLegLoads = filteredRecords;
    this.dataToUpdate.dailyLegLoads.push(cloneDeep(row));
    this.debounceActive = true;

    this.$confirmedChange.next(this.dataToUpdate);
  }

  async loadGrid(transactionId?: number) {
    const ptDate = await this.dailyLogService.getCurrentLogDay();

    if (ptDate != null) {
      this.dailyLogDate = cloneDeep(ptDate);
    }

    await this.checkImplementerActive();
    this.checkDayCompleted();
    await this.dailyLegLoadService.updateLegLoadLogDataForDate(this.dailyLogDate);
    await this.permissionService.updateLockedDayFunctionalPermissions(this.lockedDayPermissions, this.dailyLogDate)

    this.dailyLegLoadService.getLegLoadLogDataForDay(this.dailyLogDate).then(data => {
      if (data == null || data.length === 0) {
        this.displayNoLoadRequiredMessage = true;
        this.loading = false;
        this.initialLoad = false;
        this.firstLoadComplete = true;
        this.debounceActive = false;
        this.dataSyncing = false;
        this.validate();
      } else {
        this.displayNoLoadRequiredMessage = false;
        this.initialLoad = false;
        this.firstLoadComplete = true;

        // don't reload if we are waiting for another api call to finish
        if (transactionId && transactionId === this.latestTransactionId || !transactionId) {
          this.dailyLegLoadData = data;
          this.buildLegColumns();
          this.loading = false;

          this.allReadingsComplete = this.dailyLegLoadData.filter(e => !e.disabled).every(e => e.statusId === 3);

          this.debounceActive = false;
          this.dataSyncing = false;
          this.validate();
        }
      }
    });
  }

  dateChanged() {
    this.initialLoad = true;
    this.firstLoadComplete = false;
    this.dailyLegLoadData = [];

    this.loadGrid();
  }

  otherPagesNotComplete(): boolean {
    if (this.validationResult.errors.some(e => e.pageValidationType != null
      && e.pageValidationType !== PageValidationType.DailyObservations
      && e.pageValidationType !== PageValidationType.FieldPositioning
      && e.pageValidationType !== PageValidationType.VoyageLog)) {
      return true;
    }

    return false;
  }

  getMinValue(allowNegative: any) {
    return allowNegative ? -9999 : 0;
  }

  // selectedText is used so we don't stop the user from being able to type in an input when they have text highlighted
  textSelected() {
    this.selectedText = true;
  }

  inputBlur() {
    this.selectedText = false;
  }

  keyPress(currentValue: number, event: KeyboardEvent, preventDecimal?: boolean, column?: any) {
    const isLeg = column?.isLeg === true;

    switch (true) {
      case isLeg:
      case column.field === 'calculatedElevatedWeight':
        if (currentValue != null
          && currentValue.toString().length >= 5
          && event.key !== 'Backspace'
          && event.key !== 'ArrowLeft'
          && event.key !== 'ArrowRight'
          && event.key !== 'Tab'
          && !this.selectedText) {
          event.preventDefault();
        }
        break;
      case column.field === 'port':
      case column.field === 'fore':
        if (currentValue != null) {
          const afterDecimal = currentValue.toString().split('.')[1];
          if (afterDecimal != null
            && afterDecimal.length >= 3
            && event.key !== 'Backspace'
            && event.key !== 'ArrowLeft'
            && event.key !== 'ArrowRight'
            && event.key !== 'Tab'
            && !this.selectedText) {
            event.preventDefault();
          }
        }
        break;
    }

    this.selectedText = false;
  }

  columnMandatory(column: any): boolean {
    return column?.isLeg === true;
  }

  // gets called for every row to selectively apply different css styles
  public assignRowSelected(context: RowClassArgs) {
    return {
      'greyed': context.dataItem.disabled
    };
  }

  openManageRecordingsPage() {
    this.navCtrl.push(DailyLegLoadsManageReadingsPage);
  }

  openLegLoadGraphPage() {
    this.navCtrl.push(DailyLegLoadGraphPage, {date: this.dailyLogDate.dateTime});
  }

  openTotalLegExtensionModel() {
    const confirmationModal = this.modalController
      .create(DailyLegLoadTotalExtensionsPage, {date: this.dailyLogDate}, {cssClass: 'daily-leg-load-total-extension-modal'});

    confirmationModal.present();
  }

  dateBeforeInitialSetupChanged($event: boolean) {
    this.dateBeforeInitialSetup = $event;
  }

  goToActiveLogDay() {
    this.dailyLogDatePickerComponent.goToActiveLogDay();
  }

  dateLoadingStarted() {
    this.firstLoadComplete = false;
  }

  dateLoadingFinished() {
    this.firstLoadComplete = true;
  }

  getLegLoad(row: DailyLegLoad, legNumber: number): number {
    return (row.legs || []).find(l => l.legNumber === legNumber)?.load ?? null;
  }

  setLegLoad(row: DailyLegLoad, legNumber: number, value: number) {
    if (!row.legs) {
      row.legs = [];
    }

    let leg = row.legs.find(l => l.legNumber === legNumber);
    if (!leg) {
      leg = { legNumber, load: null };
      row.legs.push(leg);
    }

    leg.load = value;
  }

  sumLegLoads(row: DailyLegLoad): number {
    return (row.legs || [])
      .map(l => l.load || 0)
      .reduce((a, b) => a + b, 0);
  }

  private buildLegColumns() {
    const legNumbers = Array.from(new Set(
      (this.dailyLegLoadData || [])
        .flatMap(r => (r.legs || []).map(l => l.legNumber))
    )).sort((a, b) => a - b);

    // Remove old hardcoded legLoad columns if they exist
    this.columns = (dailyLegLoadColumns || []).filter(c =>
      !['legLoad1', 'legLoad2', 'legLoad3', 'legLoad4'].includes(c.field)
    );

    // Add dynamic leg columns
    legNumbers.forEach(n => {
      this.columns.splice(2 + (n - 1), 0, {
        field: `leg_${n}`,
        title: `Leg Load ${n}`,
        filter: 'numeric',
        locked: false,
        width: 120,
        isLeg: true,
        legNumber: n
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/pages/daily-leg-loads/daily-leg-loads-log-component/daily-leg-loads-log-component.ts